import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import moment from 'moment';
import {
  Row,
  Col,
  Typography,
  message,
  Modal,
  Form,
  Drawer,
  Tag,
  Input
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faHeart } from '@fortawesome/free-solid-svg-icons';
import { SearchOutlined } from '@ant-design/icons';
import EventTypeService from '../../services/EventTypeService';
import BookingService from '../../services/BookingService';
import { getEventTypeColumns } from './components/EventTypeColumns';
import EventTypeFormModal from './components/EventTypeFormModal';
import EventTypeFilterDrawer from './components/EventTypeFilterDrawer';
import EventTypeSearchBar from './components/EventTypeSearchBar';
import {
  ActionButton,
  BookingTable,
  TabContentWrapper,
  AnimatedFiltersRow,
  AnimatedTableSection
} from './NewStyled';
import HeartLoader from '../../components/shared/HeartLoader';

const { Title } = Typography;

const EventTypeManager = () => {
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEventType, setEditingEventType] = useState(null);
  const [form] = Form.useForm();
  const [selectedDays, setSelectedDays] = useState([1, 2, 3, 4, 5]);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: undefined,
    color: undefined
  });
  // Monthly cap states
  const [monthlyCaps, setMonthlyCaps] = useState([]);
  const [defaultMonthlyCapEnabled, setDefaultMonthlyCapEnabled] = useState(false);
  const [defaultMonthlyCap, setDefaultMonthlyCap] = useState(null);
  const [dailyCapacityRules, setDailyCapacityRules] = useState([]);
  const [autoCloseNextMonth, setAutoCloseNextMonth] = useState({ enabled: false, cutoffDay: 15 });

  // Bookings drawer states
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState(null);
  const [drawerBookings, setDrawerBookings] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerSearch, setDrawerSearch] = useState('');
  const [drawerPagination, setDrawerPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  useEffect(() => {
    loadEventTypes();
  }, []);

  const loadEventTypes = async () => {
    try {
      setLoading(true);
      const response = await EventTypeService.getEventTypes();
      if (response.success) {
        setEventTypes(response.data.eventTypes || []);
      } else {
        message.error('Failed to load event types');
      }
    } catch (error) {
      console.error('Error loading event types:', error);
      message.error('Unable to load event types');
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (eventType) => {
    setSelectedEventType(eventType);
    setDrawerSearch('');
    setDrawerPagination({ current: 1, pageSize: 10, total: 0 });
    setDrawerVisible(true);
    loadDrawerBookings(eventType._id, 1, 10, '');
  };

  const loadDrawerBookings = async (eventTypeId, page = 1, limit = 10, search = '') => {
    try {
      setDrawerLoading(true);
      const response = await BookingService.getAllBookings({
        eventTypeId,
        page,
        limit,
        search,
        includeAllStatuses: true,
        sortField: 'bookingDate',
        sortOrder: 'desc'
      });
      if (response.success) {
        setDrawerBookings(response.data.bookings || []);
        setDrawerPagination(prev => ({
          ...prev,
          current: page,
          total: response.data.pagination?.total || 0
        }));
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
      message.error('Failed to load bookings');
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleDrawerTableChange = (pagination) => {
    setDrawerPagination(prev => ({ ...prev, current: pagination.current, pageSize: pagination.pageSize }));
    loadDrawerBookings(selectedEventType._id, pagination.current, pagination.pageSize, drawerSearch);
  };

  const handleDrawerSearch = (value) => {
    setDrawerSearch(value);
    setDrawerPagination(prev => ({ ...prev, current: 1 }));
    loadDrawerBookings(selectedEventType._id, 1, drawerPagination.pageSize, value);
  };

  const drawerColumns = [
    {
      title: 'Event Name',
      key: 'eventName',
      width: 150,
      render: (_, record) => (
        <div style={{ fontWeight: 'bold' }}>
          {record.eventLocation?.eventName || record.userInfo?.customData?.businessEventName || '-'}
        </div>
      )
    },
    {
      title: 'Client',
      key: 'client',
      width: 200,
      render: (_, record) => (
        <div>
          <div>
            {record.userInfo?.firstName} {record.userInfo?.lastName}
          </div>
          <div style={{ color: '#666', fontSize: '12px' }}>
            {record.userInfo?.email}
          </div>
          <div style={{ color: '#666', fontSize: '12px' }}>
            {record.userInfo?.phone}
          </div>
        </div>
      )
    },
    {
      title: 'Event Time',
      key: 'bookingDate',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {moment(record.bookingDate).format('MMM D, YYYY')}
            {record.endDate && record.isMultiDay && (
              <span> – {moment(record.endDate).format('MMM D, YYYY')}</span>
            )}
          </div>
          <div style={{ color: '#666' }}>
            {record.timeSlot ?
              `${moment(record.timeSlot.startTime).format('h:mm A')} - ${moment(record.timeSlot.endTime).format('h:mm A')}` :
              moment(record.bookingDate).format('h:mm A')
            }
          </div>
        </div>
      )
    },
    {
      title: 'Members',
      key: 'members',
      width: 90,
      render: (_, record) => (
        <div style={{ textAlign: 'center', fontWeight: 600 }}>
          {record.membersScreened || 0}
        </div>
      )
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: (_, record) => {
        const colorMap = {
          confirmed: 'blue',
          pending: 'gold',
          completed: 'green',
          cancelled: 'red',
          'no-show': 'orange'
        };
        return <Tag color={colorMap[record.status] || 'default'}>{record.status}</Tag>;
      }
    }
  ];

  const handleCreate = () => {
    setEditingEventType(null);
    form.resetFields();
    setSelectedDays([1, 2, 3, 4, 5]);
    setMonthlyCaps([]);
    setDefaultMonthlyCapEnabled(false);
    setDefaultMonthlyCap(null);
    setDailyCapacityRules([]);
    setAutoCloseNextMonth({ enabled: false, cutoffDay: 15 });
    form.setFieldsValue({
      startTime: dayjs('09:00', 'HH:mm'),
      endTime: dayjs('19:00', 'HH:mm'),
      availableDays: [1, 2, 3, 4, 5],
      timezone: 'America/Chicago'
    });
    setModalVisible(true);
  };

  const handleEdit = (eventType) => {
    setEditingEventType(eventType);
    const availableDays = eventType.availability?.availableDays || [1, 2, 3, 4, 5];
    setSelectedDays(availableDays);
    setMonthlyCaps(eventType.monthlyCaps || []);
    setDefaultMonthlyCapEnabled(eventType.defaultMonthlyCapEnabled || false);
    setDefaultMonthlyCap(eventType.defaultMonthlyCap || null);
    setDailyCapacityRules(eventType.dailyCapacityRules || []);
    setAutoCloseNextMonth({
      enabled: eventType.autoCloseNextMonth?.enabled || false,
      cutoffDay: eventType.autoCloseNextMonth?.cutoffDay || 15
    });
    
    const startTime = eventType.availability?.startTime 
      ? dayjs(eventType.availability.startTime, 'HH:mm')
      : dayjs('09:00', 'HH:mm');
    const endTime = eventType.availability?.endTime 
      ? dayjs(eventType.availability.endTime, 'HH:mm')
      : dayjs('19:00', 'HH:mm');
    
    form.setFieldsValue({
      name: eventType.name,
      description: eventType.description,
      city: eventType.city || '',
      state: eventType.state || '',
      timezone: eventType.timezone || 'America/Chicago',
      duration: eventType.duration,
      color: eventType.color,
      maxBookingsPerDay: eventType.maxBookingsPerDay,
      advanceBookingDays: eventType.advanceBookingDays,
      bufferTime: eventType.bufferTime,
      publiclyVisible: eventType.publiclyVisible !== false,
      availableDays: availableDays,
      startTime: startTime,
      endTime: endTime
    });
    setModalVisible(true);
  };

  const handleDelete = async (eventTypeId) => {
    try {
      const response = await EventTypeService.deleteEventType(eventTypeId);
      if (response.success) {
        message.success('Event type deleted successfully');
        loadEventTypes();
      } else {
        message.error('Failed to delete event type');
      }
    } catch (error) {
      console.error('Error deleting event type:', error);
      message.error('Unable to delete event type');
    }
  };

  const handleSubmit = async (values) => {
    try {
      const colorValue = typeof values.color === 'string' ? values.color : '#1890ff';

      const availabilityData = {
        availableDays: values.availableDays || [],
        startTime: values.startTime ? values.startTime.format('HH:mm') : null,
        endTime: values.endTime ? values.endTime.format('HH:mm') : null
      };

      const eventTypeData = {
        name: values.name,
        description: values.description,
        city: values.city || null,
        state: values.state || null,
        timezone: values.timezone || 'America/Chicago',
        duration: values.duration,
        color: colorValue,
        icon: values.icon,
        advanceBookingDays: values.advanceBookingDays,
        maxBookingsPerDay: values.maxBookingsPerDay,
        bufferTime: values.bufferTime,
        welcomeMessage: values.welcomeMessage,
        availability: availabilityData,
        customFields: [],
        organizationId: '507f1f77bcf86cd799439011', // TODO: Get from auth context
        isActive: true,
        publiclyVisible: values.publiclyVisible !== false,
        monthlyCaps: monthlyCaps,
        defaultMonthlyCapEnabled: defaultMonthlyCapEnabled,
        defaultMonthlyCap: defaultMonthlyCap,
        dailyCapacityRules: dailyCapacityRules,
        autoCloseNextMonth: autoCloseNextMonth
      };

      console.log('Submitting event type data:', eventTypeData);

      let response;
      if (editingEventType) {
        response = await EventTypeService.updateEventType(editingEventType._id, eventTypeData);
      } else {
        response = await EventTypeService.createEventType(eventTypeData);
      }

      if (response.success) {
        message.success(`Event type ${editingEventType ? 'updated' : 'created'} successfully`);
        setModalVisible(false);
        loadEventTypes();
      } else {
        message.error(`Failed to ${editingEventType ? 'update' : 'create'} event type`);
      }
    } catch (error) {
      console.error('Error saving event type:', error);
      message.error('Unable to save event type');
    }
  };

  const handleDeleteConfirm = (eventType) => {
    Modal.confirm({
      title: 'Delete Event Type',
      content: `Are you sure you want to delete "${eventType.name}"?`,
      onOk: () => handleDelete(eventType._id)
    });
  };

  // Filter event types
  const filteredEventTypes = eventTypes.filter(eventType => {
    const matchesSearch = filters.search === '' || 
      eventType.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      (eventType.description && eventType.description.toLowerCase().includes(filters.search.toLowerCase()));
    
    const matchesStatus = filters.status === undefined || 
      (filters.status === 'active' && eventType.isActive) ||
      (filters.status === 'inactive' && !eventType.isActive);

    return matchesSearch && matchesStatus;
  });

  const columns = getEventTypeColumns(handleEdit, handleDeleteConfirm);

  return (
    <TabContentWrapper>
      <Row gutter={[0, 16]}>
        <Col span={24}>
          <AnimatedFiltersRow>
            <EventTypeSearchBar
              searchValue={filters.search}
              onSearchChange={(value) => setFilters({ ...filters, search: value })}
              hasActiveFilters={!!filters.status}
              onFilterClick={() => setFilterDrawerVisible(true)}
              onCreateClick={handleCreate}
            />
          </AnimatedFiltersRow>
        </Col>

        <Col span={24}>
          <AnimatedTableSection style={{ position: 'relative' }}>
            {loading && (
              <div style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 10, minHeight: '200px'
              }}>
                <HeartLoader size={48} />
              </div>
            )}
            <BookingTable
              columns={columns}
              dataSource={filteredEventTypes}
              rowKey="_id"
              className="event-management-pagination"
              onRow={(record) => ({
                onClick: (e) => {
                  // Don't open drawer when clicking action buttons
                  if (e.target.closest('.ant-dropdown-trigger') || e.target.closest('.ant-dropdown')) return;
                  handleRowClick(record);
                },
                style: { cursor: 'pointer' }
              })}
              scroll={{ x: 800 }}
              pagination={{
                total: filteredEventTypes.length,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} event types`,
                pageSizeOptions: ['10', '20', '50', '100']
              }}
              locale={{
                emptyText: (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <FontAwesomeIcon icon={faHeart} style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                    <Title level={4} type="secondary">No Event Types Yet</Title>
                    <p style={{ color: '#666', marginBottom: '24px' }}>
                      Create your first event type to start accepting appointments
                    </p>
                    <ActionButton type="primary" icon={<FontAwesomeIcon icon={faPlus} />} onClick={handleCreate}>
                      Create Your First Event Type
                    </ActionButton>
                  </div>
                )
              }}
            />
          </AnimatedTableSection>
        </Col>
      </Row>

      <EventTypeFormModal
        visible={modalVisible}
        editingEventType={editingEventType}
        form={form}
        selectedDays={selectedDays}
        setSelectedDays={setSelectedDays}
        monthlyCaps={monthlyCaps}
        setMonthlyCaps={setMonthlyCaps}
        defaultMonthlyCapEnabled={defaultMonthlyCapEnabled}
        setDefaultMonthlyCapEnabled={setDefaultMonthlyCapEnabled}
        defaultMonthlyCap={defaultMonthlyCap}
        setDefaultMonthlyCap={setDefaultMonthlyCap}
        dailyCapacityRules={dailyCapacityRules}
        setDailyCapacityRules={setDailyCapacityRules}
        autoCloseNextMonth={autoCloseNextMonth}
        setAutoCloseNextMonth={setAutoCloseNextMonth}
        onSubmit={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          setSelectedDays([1, 2, 3, 4, 5]);
        }}
      />

      <EventTypeFilterDrawer
        visible={filterDrawerVisible}
        filters={filters}
        onFiltersChange={setFilters}
        onClose={() => setFilterDrawerVisible(false)}
      />

      <Drawer
        title={
          <div>
            <div style={{ fontWeight: 700, fontSize: '18px' }}>
              {selectedEventType?.name}
            </div>
            <div style={{ fontSize: '13px', color: '#666', fontWeight: 400 }}>
              All bookings for this event type
            </div>
          </div>
        }
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        width={900}
        destroyOnClose
      >
        <div style={{ marginBottom: '16px' }}>
          <Input
            placeholder="Search by name, email, or event name..."
            prefix={<SearchOutlined />}
            value={drawerSearch}
            onChange={(e) => handleDrawerSearch(e.target.value)}
            allowClear
            style={{ maxWidth: '400px' }}
          />
        </div>
        <BookingTable
          columns={drawerColumns}
          dataSource={drawerBookings}
          rowKey="_id"
          loading={drawerLoading}
          className="event-management-pagination"
          onChange={handleDrawerTableChange}
          scroll={{ x: 800 }}
          pagination={{
            current: drawerPagination.current,
            pageSize: drawerPagination.pageSize,
            total: drawerPagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} bookings`,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
        />
      </Drawer>
    </TabContentWrapper>
  );
};

export default EventTypeManager;
